import { normalizePronunciationCharacters, readingAidEngineFor } from '../../reading_aids/reading_aid_languages';
import {
  CLIENT_PROVIDERS,
  CHAT_MODELS,
  getTranscriptionModes,
  AI_MODEL_OPTIONS,
  VOICE_MODEL_OPTIONS
} from './constants';
import { SPEED_MULTIPLIER } from '../../constants/speedMultiplier';

class ChatOptions {
  constructor(options) {
    const {
      chatId,
      language,
      languageLabel,
      languageCode,
      targetLanguage,
      mode,
      voice,
      voiceEnabled,
      voiceProvider,
      ttsModel,
      voiceGender,
      voiceSpeed,
      speedMultiplier,
      model,
      clientProvider,
      streamingEnabled = false,
      autoRecord = false,
      autoSend = false,
      autoCorrect = false,
      repeatCorrections = false,
      verbalCorrectionMode = null,
      guidedMode = false,
      autoTranslate,
      debug = false,
      messageCount,
      transcriptionMode = 'v1',
      aiModel = 'v1',
      dialectCode,
      voiceModel,
      beta_features_enabled = false,
      voxtralDefaultEnabled = false,
      sonioxDefaultEnabled = false,
      sonioxRealtimeEnabled = false,
      pronunciationCharacters,
      pronunciationCharactersOptions = null,
      disableGuidedMode = false,
      speedSliderEnabled = false,
      streamLanguageLevel = null,
      isBeginnerOrBasic = false,
      highlightMode = "sentence",
      transcriptionUrl = null,
      userUuid = null,
      workerEnabled = false,
      workerFeatureEnabled = false,
      aiModelOptions: aiModelOptionsRaw = null,
      aiModelHelpText = null,
      disableAiModelSelection = false,
      correctionModeSettingsEnabled = false,
      verbalCorrectionOptionsEnabled = false,
      transcriptionCleanup = false,
      multipleChoiceExercise = false,
      transcriptionPostProcessingEnabled = false,
      onSettingsUpdated = null
    } = options;

    this.chatId = chatId;
    this.language = language;
    this.languageLabel = languageLabel;
    this.languageCode = languageCode;
    this.targetLanguage = targetLanguage;
    this.dialectCode = dialectCode;
    this.mode = mode;
    this.voice = voice;
    this.voiceEnabled = voiceEnabled;
    this.voiceProvider = voiceProvider;
    // Inworld model chosen by Rails (null for other providers)
    this.ttsModel = ttsModel || null;
    this.voiceGender = voiceGender;
    this.voiceSpeed = voiceSpeed;
    this.speedMultiplier = speedMultiplier;
    this.streamingEnabled = streamingEnabled;
    this.autoRecord = autoRecord;
    this.autoSend = autoSend;
    this.autoCorrect = autoCorrect;
    this.repeatCorrections = repeatCorrections;
    this.verbalCorrectionMode = verbalCorrectionMode;
    this.guidedMode = guidedMode;
    this.autoTranslate = autoTranslate;
    this.debug = debug;
    this.messageCount = messageCount;
    this.model = model;
    this.clientProvider = clientProvider;
    this.chatClientProviderOptions = CLIENT_PROVIDERS;
    this.chatModelOptions = CHAT_MODELS;
    this.voxtralDefaultEnabled = voxtralDefaultEnabled === true || voxtralDefaultEnabled === "true";
    this.sonioxDefaultEnabled = sonioxDefaultEnabled === true || sonioxDefaultEnabled === "true";
    this.sonioxRealtimeEnabled = sonioxRealtimeEnabled === true || sonioxRealtimeEnabled === "true";
    const transcriptionModes = getTranscriptionModes(this.voxtralDefaultEnabled, beta_features_enabled === true || beta_features_enabled === "true", this.sonioxDefaultEnabled, this.sonioxRealtimeEnabled);
    // Map mode.value (and voxtral/soniox context for v1) to a stable i18n key under javascript.chat.transcription_modes.
    const transcriptionLabelKey = (mode) => {
      if (mode.value === 'v1') return (this.sonioxRealtimeEnabled || this.sonioxDefaultEnabled || this.voxtralDefaultEnabled) ? 'v1_fastest' : 'v1_recommended';
      const keys = { v2: 'v2', v3: 'v3', v4: 'v4_scribe_v2', v5: 'v5_gpt4o_audio', v6: 'v6_whisper_turbo', v7: 'v7_gemini_pro', v8: 'v8_deepgram', v9: 'v9_elevenlabs', v10: 'v10_speechmatics' };
      return keys[mode.value];
    };
    this.chatTranscriptionModes = Object.values(transcriptionModes).map(mode => {
      const key = transcriptionLabelKey(mode);
      return { label: key ? I18n.t(`javascript.chat.transcription_modes.${key}`) : mode.label, value: mode.value };
    });
    this.transcriptionMode = transcriptionMode;
    this.aiModelOptions = this.normalizeAiModelOptions(aiModelOptionsRaw);
    this.aiModelOptionMap = this.buildAiModelOptionMap(this.aiModelOptions);
    this.aiModel = aiModel; // Default to provided value
    this.voiceModel = voiceModel;
    this.voiceModelOptions = [
      { label: I18n.t('javascript.chat.voice_model_options.v1_recommended'), value: 'v1' },
      { label: I18n.t('javascript.chat.voice_model_options.v2'), value: 'v2' }
    ];
    this.canChangeVoiceModel = true;
    this.pronunciationCharacters = normalizePronunciationCharacters(pronunciationCharacters);
    this.pronunciationCharactersOptions = pronunciationCharactersOptions ? JSON.parse(pronunciationCharactersOptions) : null;
    this.speedSliderEnabled = speedSliderEnabled;
    this.disableGuidedMode = disableGuidedMode;
    this.streamLanguageLevel = streamLanguageLevel;
    this.isBeginnerOrBasic = isBeginnerOrBasic;
    this.highlightMode = highlightMode;
    this.transcriptionUrl = transcriptionUrl;
    this.userUuid = userUuid;
    // Convert string to boolean for workerEnabled
    this.workerEnabled = workerEnabled === true || workerEnabled === 'true';

    // Disable guided mode if in a restricted category
    if (this.disableGuidedMode) {
      this.guidedMode = false;
    }

    // Store whether AI model selection should be disabled
    this.disableAiModelSelection = disableAiModelSelection;
    this.correctionModeSettingsEnabled = correctionModeSettingsEnabled === true || correctionModeSettingsEnabled === 'true';
    this.verbalCorrectionOptionsEnabled = verbalCorrectionOptionsEnabled === true || verbalCorrectionOptionsEnabled === 'true';

    // Transcription post-processing
    this.transcriptionCleanup = transcriptionCleanup;
    this.multipleChoiceExercise = multipleChoiceExercise;
    this.transcriptionPostProcessingEnabled = transcriptionPostProcessingEnabled;

    // Optional hook for live chat transports (notably the Cloudflare Worker)
    // to refresh their cached context after settings/prompt changes.
    this.onSettingsUpdated = onSettingsUpdated;

    this.updateAiModelHelpText(this.aiModel, aiModelHelpText);
  }

  setupEventListeners() {
    // Bind the event listener methods to the instance
    this.handleAutoRecordChange = this.handleAutoRecordChange.bind(this);
    this.handleAutoSendChange = this.handleAutoSendChange.bind(this);
    this.handleCorrectionModeChange = this.handleCorrectionModeChange.bind(this);
    this.handleGuidedModeChange = this.handleGuidedModeChange.bind(this);
    this.handleMicrophoneChange = this.handleMicrophoneChange.bind(this);
    this.handleSoundOutputChange = this.handleSoundOutputChange.bind(this);
    this.handleTranscriptionModeChange = this.handleTranscriptionModeChange.bind(this);
    this.handleAiModelChange = this.handleAiModelChange.bind(this);
    this.handleVoiceModelChange = this.handleVoiceModelChange.bind(this);
    this.handlePronunciationCharactersChange = this.handlePronunciationCharactersChange.bind(this);
    this.handleSpeedMultiplierChange = this.handleSpeedMultiplierChange.bind(this);
    this.handleHighlightModeChange = this.handleHighlightModeChange.bind(this);
    this.handleTranscriptionCleanupChange = this.handleTranscriptionCleanupChange.bind(this);

    // Add event listeners to the form inputs
    document.getElementById('chatAutoRecord').addEventListener('change', this.handleAutoRecordChange);
    document.getElementById('chatAutoSend').addEventListener('change', this.handleAutoSendChange);
    if (this.correctionModeSettingsEnabled && document.getElementById('chatCorrectionMode')) {
      document.getElementById('chatCorrectionMode').addEventListener('change', this.handleCorrectionModeChange);
    }
    if (!this.disableGuidedMode) {
      document.getElementById('chatGuidedMode').addEventListener('change', this.handleGuidedModeChange);
    }
    document.getElementById('chatMicrophone').addEventListener('change', this.handleMicrophoneChange.bind(this));
    document.getElementById('chatSoundOutput').addEventListener('change', this.handleSoundOutputChange.bind(this));
    document.getElementById('chatTranscriptionMode').addEventListener('change', this.handleTranscriptionModeChange);
    if (!this.disableAiModelSelection) {
      document.getElementById('chatAiModel').addEventListener('change', this.handleAiModelChange);
    }
    document.getElementById('chatVoiceModel').addEventListener('change', this.handleVoiceModelChange);
    document.getElementById('chatHighlightMode').addEventListener('change', this.handleHighlightModeChange.bind(this));

    const transcriptionCleanupEl = document.getElementById('chatTranscriptionCleanup');
    if (transcriptionCleanupEl) {
      transcriptionCleanupEl.addEventListener('change', this.handleTranscriptionCleanupChange);
    }

    // Check if chatPronunciationCharacters exists before adding event listener
    const pronunciationElement = document.getElementById('chatPronunciationCharacters');
    if (pronunciationElement) {
      pronunciationElement.addEventListener('change', this.handlePronunciationCharactersChange);
    }

    if (this.speedSliderEnabled && document.getElementById('speed-multiplier-slider')) {
      document.getElementById('speed-multiplier-slider').addEventListener('change', this.handleSpeedMultiplierChange.bind(this));
    }

    // Check if chatPronunciationCharacters exists before adding event listener
    const pronunciationCharactersElement = document.getElementById('chatPronunciationCharacters');
    if (pronunciationCharactersElement) {
      pronunciationCharactersElement.addEventListener('change', this.handlePronunciationCharactersChange);
    }


    // Set up chevron rotation for collapsible section
    $('#advancedSettingsCollapse').on('show.bs.collapse', function () {
      $('.chevron-icon').css('transform', 'rotate(90deg)');
    });

    $('#advancedSettingsCollapse').on('hide.bs.collapse', function () {
      $('.chevron-icon').css('transform', 'rotate(0deg)');
    });

    // listen for modal close event
    $('#chatOptionsModal').on('hidden.bs.modal', this.closeModal);
  }

  setupInfoIconsEventListeners() {
    $('[data-toggle="tooltip"]').tooltip({
      trigger: 'hover click',
      placement: 'auto',
      container: 'body'
    });

    $('.js-info-icon').on('click touchstart', function(e) {
      e.preventDefault();
      e.stopPropagation();
    });

    // Handle both touch and click events
    $('[data-toggle="tooltip"]').on('touchstart click', function(e) {
      // Prevent double-firing on mobile devices
      if (e.type === 'click' && e.originalEvent.type === 'click') {
        return;
      }

      e.preventDefault();
      const $currentTooltip = $(this);

      // Hide all other tooltips first
      $('[data-toggle="tooltip"]').not(this).tooltip('hide');

      // Toggle current tooltip
      if ($currentTooltip.attr('aria-describedby')) {
        $currentTooltip.tooltip('hide');
      } else {
        $currentTooltip.tooltip('show');
      }
    });

    // Hide tooltip when clicking/touching anywhere else
    $(document).on('touchstart click', function(e) {
      if (!$(e.target).hasClass('js-info-icon') && !$(e.target).parents('.tooltip').length) {
        $('[data-toggle="tooltip"]').tooltip('hide');
      }
    });
  }

  // Event listener method for auto record checkbox change
  handleAutoRecordChange(event) {
    this.updateOption('autoRecord', event.target.checked);
  }

  // Event listener method for auto send checkbox change
  handleAutoSendChange(event) {
    this.updateOption('autoSend', event.target.checked);
  }

  getCorrectionMode() {
    if (this.autoCorrect) return 'written';
    if (this.verbalCorrectionMode === 'subtle') return 'verbal_subtle';
    if (this.verbalCorrectionMode === 'explicit') return 'verbal_explicit';
    if (this.verbalCorrectionMode === 'repeat' || this.repeatCorrections) return 'verbal_repeat';
    return 'off';
  }

  correctionModeSettings(mode) {
    switch (mode) {
      case 'written':
        return { autoCorrect: true, repeatCorrections: false, verbalCorrectionMode: null };
      case 'verbal_subtle':
        return { autoCorrect: false, repeatCorrections: false, verbalCorrectionMode: 'subtle' };
      case 'verbal_explicit':
        return { autoCorrect: false, repeatCorrections: false, verbalCorrectionMode: 'explicit' };
      case 'verbal_repeat':
        return { autoCorrect: false, repeatCorrections: true, verbalCorrectionMode: 'repeat' };
      default:
        return { autoCorrect: false, repeatCorrections: false, verbalCorrectionMode: null };
    }
  }

  handleCorrectionModeChange(event) {
    this.updateCorrectionModeHelpText(event.target.value);
    this.updateOptions(this.correctionModeSettings(event.target.value));
  }

  // Event listener method for guided mode checkbox change
  handleGuidedModeChange(event) {
    this.updateOption('guidedMode', event.target.checked);
    // Dispatch a custom event so ChatService can react immediately
    document.dispatchEvent(new CustomEvent('guidedModeToggled', { detail: { enabled: event.target.checked } }));
  }

  handleAiModelChange(event) {
    const newAIModel = event.target.value;
    const option = this.aiModelOptionMap[newAIModel];

    if (option && option.clientProvider && option.model) {
      this.updateClientProviderAndModel(newAIModel, option.clientProvider, option.model);
    } else {
      this.updateOption('ai_model', newAIModel);
    }

    this.aiModel = newAIModel;
    this.updateAiModelHelpText(newAIModel);
  }

  async handleMicrophoneChange(event) {
    this.selectedMicrophone = event.target.value;
    if (this.audioRecordingService && typeof this.audioRecordingService.updateMicrophone === 'function') {
      try {
        await this.audioRecordingService.updateMicrophone(this.selectedMicrophone);
      } catch (error) {
        console.error('Error updating microphone:', error);
      }
    }
  }

  resetMicrophoneSelection() {
    this.selectedMicrophone = 'default';
    const dropdown = document.getElementById('chatMicrophone');
    if (dropdown) dropdown.value = 'default';
  }

  handleSoundOutputChange(event) {
    this.selectedSoundOutput = event.target.value;
    this.audioPlayerService.updateSoundOutput(this.selectedSoundOutput);
  }

  async populateDeviceDropdowns() {
    if (this.audioRecordingService && !this.audioRecordingService.microphoneAvailable()) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphoneDropdown = document.getElementById('chatMicrophone');
      const soundOutputDropdown = document.getElementById('chatSoundOutput');

      const audioInputDevices = devices.filter(device => device.kind === 'audioinput');
      const audioOutputDevices = devices.filter(device => device.kind === 'audiooutput');

      // Use sets to track added groupIds
      const uniqueMicrophoneGroups = new Set();
      const uniqueSpeakerGroups = new Set();

      // Helper function to add options to a dropdown
      function addOption(dropdown, value, text, selectedValue) {
        const option = document.createElement('option');
        option.value = value;
        option.text = text;
        if (value === selectedValue) {
          option.selected = true;
        }
        dropdown.appendChild(option);
      }

      // Clear existing options
      microphoneDropdown.innerHTML = '';
      soundOutputDropdown.innerHTML = '';

      if (audioInputDevices.length === 0) {
        const option = document.createElement('option');
        option.text = 'Default';
        option.value = 'default'
        microphoneDropdown.appendChild(option);
      } else {
        // Determine the default microphone label
        const defaultMicrophone = audioInputDevices.find(device => device.deviceId === 'default') || audioInputDevices[0];
        const defaultMicrophoneLabel = defaultMicrophone ? `${defaultMicrophone.label}` : 'Default Microphone';

        if (defaultMicrophone.groupId !== undefined) {
          uniqueMicrophoneGroups.add(defaultMicrophone.groupId);
        }

        addOption(microphoneDropdown, 'default', defaultMicrophoneLabel, this.selectedMicrophone);

        audioInputDevices.forEach(device => {
          if (device.groupId !== undefined) {
            if (!uniqueMicrophoneGroups.has(device.groupId) && device.deviceId !== 'default') {
              uniqueMicrophoneGroups.add(device.groupId);
              addOption(microphoneDropdown, device.deviceId, device.label || `Microphone ${microphoneDropdown.length + 1}`, this.selectedMicrophone);
            }
          } else {
            addOption(soundOutputDropdown, device.deviceId, device.label || `Speaker ${soundOutputDropdown.length + 1}`, this.selectedSoundOutput);
          }
        });
      }

      if (audioOutputDevices.length === 0) {
        const option = document.createElement('option');
        option.text = I18n.t('javascript.chat.default_speaker');
        option.value = 'default'
        soundOutputDropdown.appendChild(option);
      } else {
        // Determine the default speaker label
        const defaultSpeaker = audioOutputDevices.find(device => device.deviceId === 'default') || audioOutputDevices[0];
        const defaultSpeakerLabel = defaultSpeaker ? `${defaultSpeaker.label}` : 'Default Speakers';

        if (defaultSpeaker.groupId !== undefined) {
          uniqueSpeakerGroups.add(defaultSpeaker.groupId);
        }

        addOption(soundOutputDropdown, 'default', defaultSpeakerLabel, this.selectedSoundOutput);

        audioOutputDevices.forEach(device => {
          if (device.groupId !== undefined) {
            if (!uniqueSpeakerGroups.has(device.groupId) && device.deviceId !== 'default') {
              uniqueSpeakerGroups.add(device.groupId);
              addOption(soundOutputDropdown, device.deviceId, device.label || `Speaker ${soundOutputDropdown.length + 1}`, this.selectedSoundOutput);
            }
          } else {
            addOption(soundOutputDropdown, device.deviceId, device.label || `Speaker ${soundOutputDropdown.length + 1}`, this.selectedSoundOutput);
          }
        });

      }
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  }

  handleTranscriptionModeChange(event) {
    this.updateOption('transcriptionMode', event.target.value);
    this.audioRecordingService.updateTranscriptionMode(event.target.value);
  }

  handleVoiceModelChange(event) {
    this.updateOption('voiceModel', event.target.value);
  }

  // Japanese and Chinese keep their existing English labels (LAN-870 changes
  // only the new transliteration languages).
  pronunciationPickerLabel() {
    const engine = readingAidEngineFor(this.language);
    if (engine === 'japanese') return 'Show Furigana / Romaji';
    if (engine === 'chinese') return 'Show Pinyin';
    return I18n.t('javascript.chat.reading_aid_transliteration_header', { defaultValue: 'Show transliteration' });
  }

  handlePronunciationCharactersChange(event) {
    const selectedMode = event.target.value;
    document.dispatchEvent(new CustomEvent('readingAidCharactersChange', { detail: selectedMode }));
    this.updateOption('pronunciationCharacters', event.target.value);
  }

  handleSpeedMultiplierChange(event) {
    const sliderValue = parseInt(event.target.value);
    let speedText;
    let speedMultiplier;

    switch (sliderValue) {
      case 0:
        speedText = I18n.t('javascript.chat.speed_slower');
        speedMultiplier = String(SPEED_MULTIPLIER.slow);
        break;
      case 2:
        speedText = I18n.t('javascript.chat.speed_faster');
        speedMultiplier = String(SPEED_MULTIPLIER.fast);
        break;
      default:
        speedText = I18n.t('javascript.chat.speed_normal');
        speedMultiplier = String(SPEED_MULTIPLIER.normal);
    }

    document.getElementById('speed-multiplier-value').textContent = speedText;
    this.updateOption('speedMultiplier', speedMultiplier);
  }

  handleTranscriptionCleanupChange(event) {
    this.transcriptionCleanup = event.target.checked;
    this.updateOption('transcriptionCleanup', event.target.checked);
  }

  handleHighlightModeChange(event) {
    this.updateOption('highlightMode', event.target.value);
    // Dispatch a custom event so ChatService can react immediately
    document.dispatchEvent(new CustomEvent('highlightModeChanged', { detail: { mode: event.target.value } }));
  }

  buildModal() {
    const modalHTML = `
      <div class="modal fade" id="chatOptionsModal" tabindex="-1" role="dialog" aria-labelledby="chatOptionsModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="chatOptionsModalLabel">${I18n.t('javascript.chat.chat_options')}</h5>
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <form>
                ${this.speedSliderEnabled ? `
                <div class="speed-multiplier-container form-group ml-2">
                  <h6>${I18n.t('javascript.chat.speed_of_replies')} <span id="speed-multiplier-value">
                    ${parseFloat(this.speedMultiplier) <= 0.80 ? I18n.t('javascript.chat.speed_slower') :
        parseFloat(this.speedMultiplier) <= 1.0 ? I18n.t('javascript.chat.speed_normal') : I18n.t('javascript.chat.speed_faster')}
                  </span></h6>
                  <div class="form-group">
                    <input type="range"
                           class="form-control-range"
                           id="speed-multiplier-slider"
                           min="0"
                           max="2"
                           step="1"
                           value="${parseFloat(this.speedMultiplier) <= 0.80 ? '0' :
        parseFloat(this.speedMultiplier) <= 1.0 ? '1' : '2'}"
                    />
                    <div class="tick-marks">
                      <div class="tick"></div>
                      <div class="tick"></div>
                      <div class="tick"></div>
                    </div>
                  </div>
                  <div class="d-flex justify-content-between">
                    <small>${I18n.t('javascript.chat.speed_slower')}</small>
                    <small>${I18n.t('javascript.chat.speed_normal')}</small>
                    <small>${I18n.t('javascript.chat.speed_faster')}</small>
                  </div>
                </div>
                ` : ''}
                ${this.pronunciationCharactersOptions && readingAidEngineFor(this.language) ? `
                  <div class="pronunciation-characters-container form-group ml-2">
                    <label for="chatPronunciationCharacters"><strong>${this.pronunciationPickerLabel()}</strong></label>
                    <select class="form-control" id="chatPronunciationCharacters">
                      ${this.pronunciationCharactersOptions.map(([label, value]) => `
                        <option value="${value}" ${this.pronunciationCharacters === value ? 'selected' : ''}>${label}</option>
                      `).join('')}
                    </select>
                  </div>
                ` : ''}
                ${!this.disableGuidedMode ? `
                <div class="form-group">
                  <span id="chatGuidedModeLabel" class="m-2"><strong>${I18n.t('javascript.chat.suggested_replies')}</strong>
                    <svg class="info-icon js-info-icon" style="fill: #047bf8; cursor: pointer;" data-toggle="tooltip" data-original-title="${I18n.t('javascript.chat.suggested_replies_tooltip')}" xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512">
                      <path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0S0 114.6 0 256S114.6 512 256 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-144c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32z"/>
                    </svg>
                  </span>
                  <label class="toggle-filters pull-right">
                    <input type="checkbox" class="form-check-input" id="chatGuidedMode" ${this.guidedMode ? 'checked' : ''}>
                    <span class="toggle-slider"></span>
                  </label>
                </div>
                ` : ''}
                <div class="form-group">
                  <span id="chatAutoRecordLabel" class="m-2"><strong>${I18n.t('javascript.chat.auto_record')}</strong>
                    <svg class="info-icon js-info-icon" style="fill: #047bf8; cursor: pointer;" data-toggle="tooltip" data-original-title="${I18n.t('javascript.chat.auto_record_tooltip')}" xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512">
                      <path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0S0 114.6 0 256S114.6 512 256 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-144c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32z"/>
                    </svg>
                  </span>
                  <label class="toggle-filters pull-right">
                    <input type="checkbox" class="form-check-input" id="chatAutoRecord" ${this.autoRecord ? 'checked' : ''}>
                    <span class="toggle-slider"></span>
                  </label>
                </div>
                <div class="form-group">
                  <span id="chatAutoSendLabel" class="m-2"><strong>${I18n.t('javascript.chat.auto_send')}</strong>
                    <svg class="info-icon js-info-icon" style="fill: #047bf8; cursor: pointer;" data-toggle="tooltip" data-original-title="${I18n.t('javascript.chat.auto_send_tooltip')}" xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512">
                      <path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0S0 114.6 0 256S114.6 512 256 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-144c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32z"/>
                    </svg>
                  </span>
                  <label class="toggle-filters pull-right">
                    <input type="checkbox" class="form-check-input" id="chatAutoSend" ${this.autoSend ? 'checked' : ''}>
                    <span class="toggle-slider"></span>
                  </label>
                </div>

                ${this.renderCorrectionModeSettings()}

                <!-- AI Model (Prominent) -->
                <div class="form-group ml-2 mt-4">
                  <label for="chatAiModel"><strong>${I18n.t('javascript.chat.ai_model')}</strong></label>
                  <select class="form-control" id="chatAiModel" ${this.disableAiModelSelection ? 'disabled' : ''}>
                    ${this.aiModelOptions.map((option) => `
                      <option ${this.aiModel === option.value ? 'selected="selected"' : ''} value="${option.value}">${option.label}</option>`).join('')}
                  </select>
                  ${this.disableAiModelSelection ? `<small class="form-text text-muted mt-1">${I18n.t('javascript.chat.custom_model_notice')}</small>` : '<small class="form-text text-muted mt-1" data-ai-model-help></small>'}
                </div>

                <div class="mt-4">
                  <div id="advancedSettingsHeader">
                    <h6 class="mb-0">
                      <button class="btn btn-advanced-settings" type="button" data-toggle="collapse" data-target="#advancedSettingsCollapse" aria-expanded="false" aria-controls="advancedSettingsCollapse">
                        <span>${I18n.t('javascript.chat.advanced_settings')}</span>
                        <svg class="chevron-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                          <path d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"/>
                        </svg>
                      </button>
                    </h6>
                  </div>
                  <div id="advancedSettingsCollapse" class="collapse" aria-labelledby="advancedSettingsHeader">
                    <div class="card-body">
                      <!-- Transcription Mode -->
                      <div class="form-group">
                        <label for="chatTranscriptionModeLabel"><strong>${I18n.t('javascript.chat.transcription_preference')}</strong></label>
                        <select class="form-control" id="chatTranscriptionMode">
                          ${this.chatTranscriptionModes.map((option) => `
                            <option ${this.transcriptionMode === option.value ? 'selected="selected"' : ''} value="${option.value}">${option.label}</option>`).join('')}
                        </select>
                      </div>

                      ${this.transcriptionPostProcessingEnabled ? `
                      <div class="form-group">
                        <span id="chatTranscriptionCleanupLabel" class="m-2"><strong>${I18n.t('javascript.chat.improve_transcription')}</strong>
                          <svg class="info-icon js-info-icon" style="fill: #047bf8; cursor: pointer;" data-toggle="tooltip" data-original-title="${I18n.t('javascript.chat.improve_transcription_tooltip')}" xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512">
                            <path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0S0 114.6 0 256S114.6 512 256 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-144c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32z"/>
                          </svg>
                        </span>
                        <label class="toggle-filters pull-right">
                          <input type="checkbox" class="form-check-input" id="chatTranscriptionCleanup" ${this.transcriptionCleanup ? 'checked' : ''}>
                          <span class="toggle-slider"></span>
                        </label>
                      </div>
                      ` : ''}

                      <!-- Highlight Mode -->
                      <div class="form-group">
                        <label for="chatHighlightMode"><strong>${I18n.t('javascript.chat.highlight_mode')}</strong><br/>
                          <small>${I18n.t('javascript.chat.highlight_mode_help')}</small>
                        </label>
                        <select class="form-control" id="chatHighlightMode">
                          <option value="off" ${this.highlightMode === "off" ? 'selected' : ''}>${I18n.t('javascript.chat.highlight_off')}</option>
                          <option value="word" ${this.highlightMode === "word" ? 'selected' : ''}>${I18n.t('javascript.chat.highlight_word')}</option>
                          <option value="sentence" ${this.highlightMode === "sentence" ? 'selected' : ''}>${I18n.t('javascript.chat.highlight_sentence')}</option>
                        </select>
                      </div>

                      ${this.canChangeVoiceModel ? `
                        <div class="form-group d-none">
                          <label for="chatVoiceModel"><strong>${I18n.t('javascript.chat.voice_model')}</strong></label>
                          <select class="form-control" id="chatVoiceModel">
                            ${this.voiceModelOptions.map((option) => `
                              <option ${this.voiceModel === option.value ? 'selected="selected"' : ''} value="${option.value}">${option.label}</option>`).join('')}
                          </select>
                        </div>
                      ` : ''}

                      <!-- Microphone (moved to bottom of advanced) -->
                      <div class="form-group">
                        <label for="chatMicrophone"><strong>${I18n.t('javascript.chat.microphone')}</strong></label>
                        <select class="form-control" id="chatMicrophone"></select>
                      </div>

                      <!-- Speaker (moved to bottom of advanced) -->
                      <div class="form-group">
                        <label for="chatSoundOutput"><strong>${I18n.t('javascript.chat.speaker')}</strong></label>
                        <select class="form-control" id="chatSoundOutput"></select>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    `;

    // Append the modal HTML to the body
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    this.updateAiModelHelpText(this.aiModel);
  };


  showModal() {
    $('#chatOptionsModal').modal('show');
  }

  renderCorrectionModeSettings() {
    if (!this.correctionModeSettingsEnabled || !this.verbalCorrectionOptionsEnabled) return '';

    const currentMode = this.getCorrectionMode();
    const verbalOptions = `
                    <option value="verbal_subtle" ${currentMode === 'verbal_subtle' ? 'selected="selected"' : ''}>${I18n.t('javascript.chat.correction_verbal_subtle')}</option>
                    <option value="verbal_explicit" ${currentMode === 'verbal_explicit' ? 'selected="selected"' : ''}>${I18n.t('javascript.chat.correction_verbal_explicit')}</option>
                    <option value="verbal_repeat" ${currentMode === 'verbal_repeat' ? 'selected="selected"' : ''}>${I18n.t('javascript.chat.correction_verbal_repeat')}</option>`;

    return `
                <div class="form-group ml-2 mt-4">
                  <label for="chatCorrectionMode"><strong>${I18n.t('javascript.chat.correction_mode')}</strong></label>
                  <select class="form-control" id="chatCorrectionMode">
                    <option value="off" ${currentMode === 'off' ? 'selected="selected"' : ''}>${I18n.t('javascript.chat.correction_none')}</option>
                    <option value="written" ${currentMode === 'written' ? 'selected="selected"' : ''}>${I18n.t('javascript.chat.correction_written')}</option>${verbalOptions}
                  </select>
                  <small class="form-text text-muted" data-correction-mode-help style="${this.correctionModeHelpText(currentMode) ? '' : 'display: none;'}">${this.correctionModeHelpText(currentMode)}</small>
                </div>`;
  }

  async openModal() {

    if (this.audioRecordingService.isRecording) {
      this.audioRecordingService.stopRecording();
    }

    // Check if the modal already exists
    if (document.getElementById('chatOptionsModal')) {
      this.showModal()
    } else {
      this.buildModal();
      await this.populateDeviceDropdowns();
      this.showModal();
      this.setupInfoIconsEventListeners();
      this.setupEventListeners();
    }
  }

  closeModal() {
    // Hide the modal
    $('#chatOptionsModal').modal('hide');

    // Remove the modal HTML from the body
    document.getElementById('chatOptionsModal').remove();
  }

  normalizeAiModelOptions(aiModelOptions) {
    let options = aiModelOptions;

    if (!options || (Array.isArray(options) && options.length === 0)) {
      options = AI_MODEL_OPTIONS;
    } else if (typeof options === 'string') {
      try {
        options = JSON.parse(options);
      } catch (error) {
        console.warn('Unable to parse AI model options from dataset.', error);
        options = AI_MODEL_OPTIONS;
      }
    }

    if (!Array.isArray(options)) {
      options = AI_MODEL_OPTIONS;
    }

    return options.map((option) => ({
      label: option.label,
      value: option.value,
      helpText: option.helpText || option.help_text || '',
      clientProvider: option.clientProvider || option.client_provider || null,
      model: option.model || null
    }));
  }

  buildAiModelOptionMap(options) {
    return options.reduce((accumulator, option) => {
      if (option && option.value) {
        accumulator[option.value] = option;
      }
      return accumulator;
    }, {});
  }

  updateAiModelHelpText(selectedValue, overrideText = null) {
    const option = selectedValue ? this.aiModelOptionMap[selectedValue] : null;
    const helpText = overrideText !== null && overrideText !== undefined
      ? overrideText
      : option && option.helpText ? option.helpText : '';
    const helpElements = document.querySelectorAll('[data-ai-model-help]');

    helpElements.forEach((element) => {
      element.textContent = helpText || '';
      element.style.display = helpText ? '' : 'none';
    });
  }

  correctionModeHelpText(mode) {
    const helpTextKeys = {
      off: { key: 'javascript.chat.correction_none_help', defaultValue: 'You can still get a feedback report after each chat.' },
      written: { key: 'javascript.chat.correction_written_help', defaultValue: 'Excluded from guided course & grammar exercises, which have corrections built in.' },
      verbal_subtle: { key: 'javascript.chat.correction_verbal_subtle_help', defaultValue: 'Subtler corrections help the conversation flow. Applies only in suitable chat categories.' },
      verbal_explicit: { key: 'javascript.chat.correction_verbal_explicit_help', defaultValue: 'Flags errors whilst maintaining conversational flow. Applies only in suitable chat categories.' },
      verbal_repeat: { key: 'javascript.chat.correction_verbal_repeat_help', defaultValue: 'Good for reinforcement. Applies only in suitable chat categories.' }
    };
    const helpText = helpTextKeys[mode];

    return helpText ? I18n.t(helpText.key, { defaultValue: helpText.defaultValue }) : '';
  }

  updateCorrectionModeHelpText(selectedValue) {
    const helpText = this.correctionModeHelpText(selectedValue);
    const helpElement = document.querySelector('[data-correction-mode-help]');

    if (!helpElement) return;

    helpElement.textContent = helpText || '';
    helpElement.style.display = helpText ? '' : 'none';
  }

  async updateOptions(options) {
    // Update local state
    Object.entries(options).forEach(([key, value]) => {
      this[key] = value;
    });

    try {
      const snakeCaseOptions = Object.entries(options).reduce((acc, [key, value]) => {
        acc[this.toSnakeCase(key)] = value;
        return acc;
      }, {});

      const response = await fetch(`/account/chat_settings/${this.chatId}`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ chat: snakeCaseOptions }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (document.getElementById('chat-form')?.dataset.serverMediatedAi === 'true') {
        document.dispatchEvent(new CustomEvent('cartus:chat-settings-saved', { detail: options }));
      }
      this.showNotification(I18n.t('javascript.success.settings_updated'), 'warning');

      if (this.onSettingsUpdated) {
        this.onSettingsUpdated({ updatedOptions: options, result });
      }

      // Refresh debug view if it exists
      this.refreshDebugView();

      return result;
    } catch (error) {
      console.error('Error updating chat settings:', error);
      this.showNotification(I18n.t('javascript.errors.settings_update_failed'), 'error');
    }
  }

  async refreshDebugView() {
    try {
      const debugContainer = document.querySelector('.chat-debug-container');
      if (!debugContainer) return; // Debug view not present on page

      // Check if we're in tab mode (chat tab exists alongside debug tabs)
      const chatTabContent = document.getElementById('chat-content');
      const isTabMode = !!chatTabContent;

      // Save currently active tab (stored in localStorage by the debug view script)
      const activeTab = localStorage.getItem('activeDebugTab');

      // Build URL with mode parameter if in tab mode
      const url = `/account/chats/${this.chatId}/debug_info${isTabMode ? '?mode=tabs_only' : ''}`;

      const response = await fetch(url, {
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
          'Accept': 'text/html',
        },
      });

      if (response.ok) {
        const html = await response.text();

        if (isTabMode) {
          // In tab mode: only refresh debug tab panes (those with data-debug-tab-pane), not the chat
          // First, remove all existing debug tab panes
          const existingDebugTabs = document.querySelectorAll('[data-debug-tab-pane="true"]');
          existingDebugTabs.forEach(tab => tab.remove());

          // Then insert the new debug tab panes after the chat content
          const tabContent = document.getElementById('debug-tab-content');
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          // Append all new debug tab panes
          while (tempDiv.firstChild) {
            tabContent.appendChild(tempDiv.firstChild);
          }
        } else {
          // In full mode: refresh entire debug container
          debugContainer.outerHTML = html;
        }

        // Trigger turbolinks:load to restore collapse states and tabs
        $(document).trigger('turbolinks:load');

        // Restore active tab if one was saved
        if (activeTab && $('#debug-tabs a[href="' + activeTab + '"]').length) {
          $('#debug-tabs a[href="' + activeTab + '"]').tab('show');
        }
      }
    } catch (error) {
      console.error('Error refreshing debug view:', error);
      // Silently fail - debug view refresh is not critical
    }
  }

  async updateOption(key, value) {
    return this.updateOptions({ [key]: value });
  }

  updateClientProviderAndModel(aiModel, newClientProvider, newModel) {
    const updatedOptions = {
      ai_model: aiModel,
      clientProvider: newClientProvider,
      model: newModel
    };
    this.updateOptions(updatedOptions);
    this.aiModel = aiModel;
    $('#chat-client-provider').val(this.clientProvider);
    $('#chat-model').val(this.model);
  }

  toSnakeCase(str) {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  }

  showNotification(message, type = 'success') {
    const notificationElement = document.createElement('div');
    notificationElement.className = `alert alert-${type} alert-dismissible fade show`;
    notificationElement.role = 'alert';
    notificationElement.innerHTML = `
      ${message}
      <button type="button" class="close" data-dismiss="alert" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    `;

    const modalBody = document.querySelector('#chatOptionsModal .modal-body');
    if (modalBody) {
      modalBody.insertAdjacentElement('afterbegin', notificationElement);

      // Automatically remove the notification after 5 seconds
      setTimeout(() => {
        notificationElement.remove();
      }, 2000);
    }
  }

}

export default ChatOptions;
